import { fmlmViewInformation } from "../../../app/api/fmlmSearchLocation";
import { BaseAction } from "../../../core/actions/BaseAction";
import { updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { FMLM_BOOKABLE_OPTIONS } from "../../../Orchestrator/Recoils/LocationAtom";
import { IFmlmJourneyResponseAtom, IFmlmLeg, IFmlmView } from "../../../Orchestrator/Types/Interface/IFmlmView";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getSearchResultAction } from "../../LogicActions/Search/GetSearchResultAction";
import { getSearchSelectedPassenger } from "../../../Lib/Utils/passenger";
import { DEFAULT_TIMEZONE, ISO_FULL_DATE_FORMAT, UTC_TIMEZONE } from "../../../config/global";
import { IOffersResponse } from "../../../Orchestrator/Types/Index";
import { GetTripOfferModalFromOffersAction } from "../../LogicActions/CommonActions/GetTripOfferModalFromOffersAction";
import { ExecutionStatus } from "../../../core/enum/ExecutionStatus";
import { IGetFmlmJourneyWithOffersActionParams, IGetFmlmJourneyWithOffersActionResponse } from "../../../Orchestrator/Types/Interface/ActionParams/GetFmlmJourneyWithOffersActionParams";
import { FmlmViewOffersResponseMapType } from "../../../Flows/FmlmDetail/Context/FmlmViewContext";
import { TrainTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";

dayjs.extend(utc);
dayjs.extend(timezone);

// For BEAM scope only
export let VALID_FMLM_STOPS = [
  "Dubai",
  "Abu Dhabi",
  "Sharjah",
  "Fujairah",
]

class GetFmlmJourneyWithOffersAction extends BaseAction<IGetFmlmJourneyWithOffersActionResponse | null, IGetFmlmJourneyWithOffersActionParams> {
  override async run(): Promise<IGetFmlmJourneyWithOffersActionResponse | null> {
    const { payload, search, trainType } = this.params!

    const fmlmViewOffersMap: FmlmViewOffersResponseMapType = new Map();
    let processedFmlmViewInformation: IFmlmView[] = [];

    try {

      const response = await fmlmViewInformation(payload);

      if (response?.answer?.success && response?.answer?.data?.outboundJourneys?.length > 0) {
        // Transform all journeys to match IFmlmView interface
        const outboundJourneys: IFmlmView[] = response?.answer?.data?.outboundJourneys
        updateRecoilState<IFmlmJourneyResponseAtom>("fmlmJourneyResponseAtom", response?.answer?.data || null);

        let uniqueOriginDestinationRouteOffersMap = new Map<string, IOffersResponse | null>();
        await Promise.all(outboundJourneys.map(async (journey: IFmlmView) => {
          let bookableValidLegs = journey.legs.filter((leg: IFmlmLeg) => FMLM_BOOKABLE_OPTIONS.includes(leg.transitMode))
          // This code will only allow to book journeys that have valid FMLM city.
          // .filter((leg: IFmlmLeg) => {
          //   if (VALID_FMLM_STOPS.includes(leg.departurePoi.name) && VALID_FMLM_STOPS.includes(leg.arrivalPoi.name)) {
          //     return true;
          //   }
          // })

          if (bookableValidLegs.length === 0) return false;

          let startingLeg = bookableValidLegs[0];
          let endingLeg = bookableValidLegs[bookableValidLegs.length - 1];

          const routeKey = `${startingLeg.departurePoi.stopCode}-${endingLeg.arrivalPoi.stopCode}`;
          
          if (!uniqueOriginDestinationRouteOffersMap.has(routeKey)) {
            uniqueOriginDestinationRouteOffersMap.set(routeKey, null);

            let departureTime = dayjs.utc(startingLeg.departureTimestampPlanned).tz(UTC_TIMEZONE, true).format(ISO_FULL_DATE_FORMAT);
            let arrivalTime;
            if (search.outboundDate && search.inboundDate) {
              if (trainType === "outbound" && search.inboundDate) {
                arrivalTime = dayjs(search.inboundDate.format()).tz(UTC_TIMEZONE, true).format(ISO_FULL_DATE_FORMAT);
              } else if (trainType === "inbound" && search.outboundDate) {
                arrivalTime = dayjs(search.outboundDate.format()).tz(UTC_TIMEZONE, true).format(ISO_FULL_DATE_FORMAT);
              }
            }

            let origin = startingLeg.departurePoi.stopCode;
            let destination = endingLeg.arrivalPoi.stopCode;
            const passenger = getSearchSelectedPassenger(search.passengers);

            getSearchResultAction.setParams({
              departureTime: departureTime,
              arrivalTime: arrivalTime,
              origin,
              destination,
              totalPassengerCount: search.passengers?.length || 0,
              passengerCounts: passenger.passengerCounts,
              isOnlyOfferReturn: true,
              trainType: trainType || TrainTypeEnum.outbound
            });
            await getSearchResultAction.execute();

            if (getSearchResultAction.status.executionStatus === ExecutionStatus.SUCCESS) {
              uniqueOriginDestinationRouteOffersMap.set(routeKey, getSearchResultAction.result?.data);
            }
          }
        }));

        processedFmlmViewInformation = await Promise.all(outboundJourneys.map(async (journey: IFmlmView) => {
          let stopLegs = journey.legs.filter((leg: IFmlmLeg) => FMLM_BOOKABLE_OPTIONS.includes(leg.transitMode))
          // This code will only allow to book journeys that have valid FMLM city
          // .filter((leg: IFmlmLeg) => {
          //   if (VALID_FMLM_STOPS.includes(leg.departurePoi.name) && VALID_FMLM_STOPS.includes(leg.arrivalPoi.name)) {
          //     return true;
          //   }
          // })

          if (stopLegs.length === 0) return journey;

          let startingLeg = stopLegs[0];
          let endingLeg = stopLegs[stopLegs.length - 1];

          const startTime = dayjs.utc(startingLeg.departureTimestampPlanned).tz(DEFAULT_TIMEZONE).format(ISO_FULL_DATE_FORMAT);
          let stopCodeKey = `${startingLeg.departurePoi.stopCode}-${endingLeg.arrivalPoi.stopCode}`
          let offerResponse = uniqueOriginDestinationRouteOffersMap.get(stopCodeKey)

          if (!!offerResponse) {
            fmlmViewOffersMap.set(journey.id, offerResponse);

            let getTripOfferModalFromOffersAction = new GetTripOfferModalFromOffersAction()
            getTripOfferModalFromOffersAction.setParams({
              trips: offerResponse?.trips || [],
              offers: offerResponse?.offers || [],
            });
            await getTripOfferModalFromOffersAction.execute();

            if (getTripOfferModalFromOffersAction.status.executionStatus === ExecutionStatus.SUCCESS) {

              const matchTrip = getTripOfferModalFromOffersAction.result?.find((trip) => {
                const tripStartTime = dayjs(trip?.trip.startTime).tz(DEFAULT_TIMEZONE);
                const targetStartTime = dayjs(startTime).tz(DEFAULT_TIMEZONE);
                return tripStartTime.hour() === targetStartTime.hour() &&
                  tripStartTime.minute() === targetStartTime.minute();
              });

              if (matchTrip) {
                return ({
                  ...journey,
                  tripOffer: matchTrip,
                })
              }
              return journey
            }
          }

          return journey;
        }));
      }
    } catch {
      return null
    } finally {
      return {
        fmlmView: processedFmlmViewInformation,
        fmlmViewOffersMap: fmlmViewOffersMap
      }
    }
  }
}

export const getFmlmJourneyWithOffersAction = new GetFmlmJourneyWithOffersAction()